import axios from 'axios';
import { classifyApiError } from './classifyApiError';

// ─── Axios type augmentation (devtools timing metadata) ───────────────────────
// Allows consoleCapture to stamp request start time on config.metadata.__iacStart
// without TypeScript complaints. Declared here (nearest to the axios import) so
// any file importing httpClient picks up the augmented type automatically.
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: { __iacStart?: number };
  }
}

const baseURL = import.meta.env.VITE_API_BASE_URL ?? 'https://api.admin.myintelli.net';

export const httpClient = axios.create({
  baseURL,
  // 120s: workaround for backend HTTP/2 END_STREAM bug on POST /enroll/device.
  // The backend sends the complete response body in ~1s but does not close the
  // HTTP/2 stream until keep-alive timeout (~60-70s). Remove once backend fixed.
  // See: docs/bug-report-enroll-device-http2-end-stream.md
  timeout: 120_000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

httpClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('mi_authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

httpClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const apiError = classifyApiError(error);

    // Token expirado → limpiar sesión y notificar a React via evento global.
    // No usamos navigate() aquí (estamos fuera de React).
    if (apiError.kind === 'authentication' && apiError.code === 'token_expired') {
      clearSession();
      window.dispatchEvent(new CustomEvent('mi:session-expired'));
    }

    // Re-lanzar como ApiError tipado. useQuery/useMutation reciben esto en .error.
    return Promise.reject(apiError);
  },
);

/** Limpia las keys de sesión sin borrar la config del terminal ni la cola de sync. */
function clearSession(): void {
  const sessionKeys = [
    'mi_authed', 'mi_profile_chosen', 'mi_authToken', 'mi_session',
    'mi_admin_session',
  ];
  sessionKeys.forEach((key) => localStorage.removeItem(key));
}

/** Ping rápido al backend para verificar conectividad real. Timeout: 5s. */
export async function pingHealthCheck(): Promise<boolean> {
  try {
    await axios.get(`${baseURL}/health_check`, {
      timeout: 5_000,
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
      },
    });
    return true;
  } catch {
    return false;
  }
}
